/**
 * Radix nested-portal guard for modal layers (Dialog / Sheet).
 *
 * Select / Popover / DropdownMenu / combobox / tooltip content — and sonner
 * toasts — render in a portal OUTSIDE the dialog's DOM subtree, so the
 * dialog's outside-interaction detection treats (a) clicks inside that
 * portaled content and (b) the outside click that merely dismisses it as
 * "outside the dialog" — and closes the whole modal. The guard tells the modal to ignore an outside interaction
 * when it targets a portaled layer OR when any such layer was open when the
 * interaction STARTED (that click's job is to dismiss the layer; the NEXT one
 * may close the modal).
 *
 * ONE LAYER PER CLICK: while a floating layer (select/popover/dropdown) is
 * open inside a modal, an outside pointerdown dismisses ONLY that layer —
 * the modal dismisses only when NO layer was open at pointerdown time,
 * regardless of where the click lands.
 *
 * The "was open" check needs a start-of-interaction snapshot: Radix's own
 * pointerdown handling closes/unmounts the nested layer BEFORE the dialog's
 * `onInteractOutside` runs, so a live DOM query at that point matches nothing
 * and the dialog would close along with the select. A capture-phase
 * `pointerdown` listener records the layer state ahead of Radix. The snapshot
 * lives for the WHOLE pointerdown→click sequence — for touch pointers Radix
 * defers the outside dispatch to the click event, a later task than
 * pointerdown, so a pointerdown-scoped reset would race it. It clears one
 * macrotask after the click (or on a timeout fallback for interactions that
 * never produce a click), and every pointerdown re-snapshots, so a LATER
 * genuine outside interaction (or a focus-driven one, which has no
 * pointerdown) falls back to the live check and closes the modal as expected.
 */

const PORTALED_LAYER = [
  '[data-radix-popper-content-wrapper]', // popper-positioned content (popover, dropdown, combobox, tooltip)
  '[data-slot="select-content"]', // item-aligned Select portals without the popper wrapper
  '[data-slot="popover-content"]',
  '[data-slot="dropdown-menu-content"]',
  '[data-slot="tooltip-content"]',
  '[data-slot="command"]', // cmdk hosted outside the modal subtree
  '[data-sonner-toaster]', // sonner toasts + their action buttons
].join(', ');

// Deliberately narrower than PORTALED_LAYER: a lingering toast (or tooltip)
// must not make a true backdrop click inert — only target-inside checks
// cover those.
const OPEN_LAYER = [
  '[data-radix-popper-content-wrapper]',
  '[data-slot="select-content"][data-state="open"]',
  '[data-slot="popover-content"][data-state="open"]',
  '[data-slot="dropdown-menu-content"][data-state="open"]',
].join(', ');

let hadOpenLayerOnPointerDown = false;
let resetTimer: number | undefined;

const scheduleReset = (delayMs: number) => {
  window.clearTimeout(resetTimer);
  resetTimer = window.setTimeout(() => {
    hadOpenLayerOnPointerDown = false;
  }, delayMs);
};

const snapshotOpenLayer = () => {
  hadOpenLayerOnPointerDown = document.querySelector(OPEN_LAYER) !== null;
  // Fallback for interactions that never complete with a click (drag-out,
  // cancelled touch): don't suppress later focus-driven dismissals forever.
  scheduleReset(1000);
};

if (typeof document !== 'undefined') {
  // Capture phase so the snapshot lands before Radix's document-level
  // pointerdown handlers dismiss the nested layer.
  document.addEventListener('pointerdown', snapshotOpenLayer, true);
  // End of interaction. Capture runs before Radix's deferred (touch) outside
  // dispatch in this click's bubble phase — clearing one macrotask later
  // keeps the snapshot visible to it; the next pointerdown re-snapshots.
  document.addEventListener('click', () => scheduleReset(0), true);
}

export const shouldIgnoreOutsideInteraction = (target: EventTarget | null): boolean => {
  if (target instanceof Element && target.closest(PORTALED_LAYER)) return true;
  if (document.querySelector(OPEN_LAYER) !== null) return true;
  return hadOpenLayerOnPointerDown;
};
